import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import {
  useLazyListDailySalesReportQuery,
  useDownloadDailySalesReportMutation,
} from "store/services/reportApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { Download, InfoOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";

const DailyTransactionReport = () => {
  const { isVendor, vendorId, viewEntity } = useAuth();
  const { reset } = useForm({
    defaultValues: {
      selectedRecords: [],
    },
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange } = useTablePagination();

  const updateParams = useCallback(
    (updates) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "date:-1");
  const [timeRange, setTimeRange] = useState(searchParams.get("timeRange") || "today");
  const [branch, setBranch] = useState(searchParams.get("branch") || "all");
  const [user, setUser] = useState(searchParams.get("user") || "all");
  const [errorMessage, setErrorMessage] = useState("");
  const [customFromDate, setCustomFromDate] = useState(
    searchParams.get("fromDate") ? new Date(searchParams.get("fromDate")) : null
  );
  const [customToDate, setCustomToDate] = useState(
    searchParams.get("toDate") ? new Date(searchParams.get("toDate")) : null
  );

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Daily Transaction Report" },
  ];

  const [fetchDailySalesReport, { data: reportRes, isLoading, isFetching, error }] =
    useLazyListDailySalesReportQuery();
  const [fetchBranches, { data: branchesData, isLoading: isBranchesLoading }] =
    useLazyGetDDLBranchListQuery();
  const [fetchUsers, { data: usersData, isLoading: isUsersLoading }] =
    useLazyGetDDLUserListQuery();
  const [downloadExcel, { isLoading: isDownloading }] =
    useDownloadDailySalesReportMutation();

  useEffect(() => {
    fetchBranches({ account: viewEntity?.entity?.account?._id, entity: viewEntity?.entity?._id });
    fetchUsers({ account: viewEntity?.entity?.account?._id, entity: viewEntity?.entity?._id });
  }, [fetchBranches, fetchUsers, viewEntity]);

  const branchOptions = [
    { label: "All Branches", value: "all" },
    ...(branchesData?.map((branch) => ({
      label: branch.label || branch.name,
      value: branch.value || branch._id,
    })) || []),
  ];

  const userOptions = [
    { label: "All Users", value: "all" },
    ...(usersData?.map((user) => ({
      label: user.label || user.name,
      value: user.value || user._id,
    })) || []),
  ];

  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment().subtract(6, "days").startOf("day").format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "oneMonth":
        return {
          fromDate: moment().subtract(1, "month").startOf("day").format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to && differenceInDays(new Date(to), new Date(from)) <= 90) {
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage("Date range cannot exceed 90 days.");
        return { fromDate: null, toDate: null };
      case "all":
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  const fetchReport = useCallback(() => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(timeRange, customFromDate, customToDate);
    const apiParams = {
      page: currentPage,
      limit: perPage,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      ...(branch !== "all" && { branch }),
      ...(user !== "all" && { user }),
      sortedBy,
      sortDirection,
      ...(isVendor && { vendorId }),
    };
    fetchDailySalesReport(apiParams);
  }, [
    fetchDailySalesReport,
    currentPage,
    perPage,
    isVendor,
    vendorId,
    viewEntity,
    timeRange,
    sortBy,
    branch,
    user,
    customFromDate,
    customToDate,
    getDateRange,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (error) {
      console.error("API Error:", error);
      setErrorMessage("Failed to fetch data. Retrying...");
      const retry = setTimeout(fetchReport, 2000);
      return () => clearTimeout(retry);
    }
  }, [error, fetchReport]);

  const clearAllFilters = () => {
    setSearchParams({});
    setTimeRange("all");
    setBranch("all");
    setUser("all");
    setCustomFromDate(null);
    setCustomToDate(null);
    setSortBy("date:-1");
    reset({ selectedRecords: [] });
    handlePageChange(1);
  };

  const handleDownloadExcel = async () => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(timeRange, customFromDate, customToDate);
    try {
      const response = await downloadExcel({
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        ...(branch !== "all" && { branch }),
        ...(user !== "all" && { user }),
        sortedBy,
        sortDirection,
        ...(isVendor && { vendorId }),
      }).unwrap();
      const url = window.URL.createObjectURL(response);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Daily_Transaction_Report_${moment().format("YYYYMMDD")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(error?.data?.message || "Failed to download Excel file");
      console.error("Failed to download Excel:", error);
    }
  };

  const columns = [
    {
      name: "Date",
      selector: (row) => row.date,
      cell: (row) => moment(row.date).format("MM-DD-YYYY"),
    },
    {
      name: "COC Collected",
      selector: (row) => row.cocCollected,
      cell: (row) => `Rs. ${row.cocCollected.toFixed(2)}`,
    },
    {
      name: "COC QR Payment",
      selector: (row) => row.cocQRPayment,
      cell: (row) => `Rs. ${(row.cocQRPayment || 0).toFixed(2)}`,
    },
    {
      name: "DC Charge",
      selector: (row) => row.dcCharge,
      cell: (row) => `Rs. ${(row.dcCharge || 0).toFixed(2)}`,
    },
    {
      name: "DC COD",
      selector: (row) => row.dcCOD,
      cell: (row) => `Rs. ${(row.dcCOD || 0).toFixed(2)}`,
    },
    {
      name: "DC QR Charge",
      selector: (row) => row.dcQRCharge,
      cell: (row) => `Rs. ${(row.dcQRCharge || 0).toFixed(2)}`,
    },
    {
      name: "DC QR COD",
      selector: (row) => row.dcQRCOD,
      cell: (row) => `Rs. ${(row.dcQRCOD || 0).toFixed(2)}`,
    },
    {
      name: "COD Transferred",
      selector: (row) => row.codTransferred,
      cell: (row) => `Rs. ${(row.codTransferred || 0).toFixed(2)}`,
    },
    {
      name: "COD Charge Collection",
      selector: (row) => row.deductChargeFromCOD,
      cell: (row) => `Rs. ${(row.deductChargeFromCOD || 0).toFixed(2)}`,
    },
    {
      name: "Total Collection",
      selector: (row) => row.rowTotal,
      cell: (row) => `Rs. ${(row.rowTotal || 0).toFixed(2)}`,
    },
    {
      name: "Action",
      cell: (row) => {
        const detailParams = new URLSearchParams();
        if (branch !== "all") detailParams.set("branch", branch);
        if (user !== "all") detailParams.set("user", user);
        return (
          <Link
            to={`/app/crm/reports/transaction-report/daily-detail/${row.date}?${detailParams.toString()}`}
          >
            <IconButton size="small" aria-label="View detail" color="primary">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Link>
        );
      },
    },
  ];

  useAppMeta("Daily Transaction Report");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ minWidth: 180 }}>
                Daily Transaction Report
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                <BaseSelect
                  label="Range"
                  value={timeRange}
                  onChange={(e) => {
                    const newRange = e.target.value;
                    setTimeRange(newRange);
                    if (newRange !== "custom") {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      updateParams({ timeRange: newRange, fromDate: null, toDate: null });
                    } else {
                      updateParams({ timeRange: newRange });
                    }
                    handlePageChange(1);
                  }}
                  options={[
                    { label: "Today", value: "today" },
                    { label: "Last 7 Days", value: "last7days" },
                    { label: "One Month", value: "oneMonth" },
                    { label: "Custom", value: "custom" },
                    // { label: "All", value: "all" },
                  ]}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  size="small"
                  sx={{ minWidth: 120 }}
                />
                {timeRange === "custom" && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <DatePicker
                      label="From"
                      value={customFromDate}
                      onChange={(newValue) => {
                        setCustomFromDate(newValue);
                        let adjustedToDate = customToDate;
                        if (customToDate && differenceInDays(customToDate, newValue) > 90) {
                          adjustedToDate = addDays(newValue, 90);
                          setCustomToDate(adjustedToDate);
                          setErrorMessage("Date range adjusted to maximum 90 days.");
                        }
                        updateParams({
                          fromDate: newValue ? newValue.toISOString() : null,
                          toDate: adjustedToDate ? adjustedToDate.toISOString() : null,
                        });
                        handlePageChange(1);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" sx={{ width: 130 }} />
                      )}
                      maxDate={customToDate || new Date()}
                      disableFuture
                    />
                    <DatePicker
                      label="To"
                      value={customToDate}
                      onChange={(newValue) => {
                        setCustomToDate(newValue);
                        let adjustedFromDate = customFromDate;
                        if (customFromDate && differenceInDays(newValue, customFromDate) > 90) {
                          adjustedFromDate = addDays(newValue, -90);
                          setCustomFromDate(adjustedFromDate);
                          setErrorMessage("Date range adjusted to maximum 90 days.");
                        }
                        updateParams({
                          fromDate: adjustedFromDate ? adjustedFromDate.toISOString() : null,
                          toDate: newValue ? newValue.toISOString() : null,
                        });
                        handlePageChange(1);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" sx={{ width: 130 }} />
                      )}
                      minDate={customFromDate}
                      maxDate={new Date()}
                      disableFuture
                    />
                  </Stack>
                )}
                <Box sx={{ minWidth: 160 }}>
                  <TableFilter
                    searchLabel="Search"
                    searchProps={{ size: "small" }}
                    disabled={isLoading || isFetching || isBranchesLoading}
                    initialFilters={0}
                    onClear={clearAllFilters}
                    showDateRange={false}
                    inputProps={{ sx: { width: 120 } }}
                  >
                    <Autocomplete
                      size="small"
                      options={branchOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value}
                      value={
                        branchOptions.find((opt) => opt.value === branch) || {
                          value: "all",
                          label: "All Branches",
                        }
                      }
                      onChange={(e, newValue) => {
                        const newBranch = newValue?.value || "all";
                        setBranch(newBranch);
                        updateParams({ branch: newBranch !== "all" ? newBranch : null });
                        handlePageChange(1);
                      }}
                      disableClearable
                      disabled={isBranchesLoading}
                      sx={{ minWidth: 200 }}
                      renderInput={(params) => <TextField {...params} label="Branch" />}
                    />
                    <Autocomplete
                      size="small"
                      options={userOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value}
                      value={
                        userOptions.find((opt) => opt.value === user) || {
                          value: "all",
                          label: "All Users",
                        }
                      }
                      onChange={(e, newValue) => {
                        const newUser = newValue?.value || "all";
                        setUser(newUser);
                        updateParams({ user: newUser !== "all" ? newUser : null });
                        handlePageChange(1);
                      }}
                      disableClearable
                      disabled={isUsersLoading || isLoading || isFetching}
                      sx={{ minWidth: 200 }}
                      renderInput={(params) => <TextField {...params} label="User" />}
                    />
                    <BaseSelect
                      fullWidth
                      label="Sort"
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        updateParams({ sortBy: e.target.value });
                        handlePageChange(1);
                      }}
                      options={[
                        { label: "Date ↓", value: "date:-1" },
                        { label: "Date ↑", value: "date:1" },
                      ]}
                      getOptionLabel={(opt) => opt.label}
                      getOptionValue={(opt) => opt.value}
                    />
                  </TableFilter>
                </Box>
                <Tooltip title="Download Excel">
                  <span>
                    <IconButton
                      onClick={handleDownloadExcel}
                      color="primary"
                      disabled={
                        isLoading || isFetching || isDownloading || !reportRes?.data?.length
                      }
                    >
                      <Download />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>

            {isLoading || isFetching || isBranchesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : reportRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Totals Summary */}
                {/* Totals Summary */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "10px",
                    mb: 2,
                    py: 1,
                  }}
                >
                  {[
                    { label: "COC Collected", value: reportRes?.totalCocCollected },
                    { label: "COC QR Payment", value: reportRes?.totalCocQRPayment },
                    { label: "DC Charge", value: reportRes?.totalDcCharge },
                    { label: "DC COD", value: reportRes?.totalDcCOD },
                    { label: "DC QR Charge", value: reportRes?.totalDcQRCharge },
                    { label: "DC QR COD", value: reportRes?.totalDcQRCOD },
                    { label: "COD Transferred", value: reportRes?.totalCodTransferred },
                    { label: "COD Charge Collection", value: reportRes?.totalDeductChargeFromCOD },
                  ].map(({ label, value }) => (
                    <Box
                      key={label}
                      sx={{
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        noWrap
                      >
                        {label}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        <Typography component="span" variant="caption" color="text.disabled" mr={0.25}>
                          Rs.
                        </Typography>
                        {(value || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box>
                  <AppTable
                    columns={columns}
                    data={reportRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={reportRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                  />
                </Box>
              </>
            )}
          </Box>
        </BreadcrumbLayout.Paper>

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default DailyTransactionReport;